import { useBoxDimensions } from '@/hooks/use-box-dimensions';
import type { ImageNaturalSize } from '@/hooks/use-image-natural-size';
import { cn } from '@/lib/utils';

interface ImageCropRectangle {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface ImageCropProps {
  src: string;
  imageCropRectangle: ImageCropRectangle;
  naturalSize: ImageNaturalSize | null;
  className?: string;
}

const calculateCropCssSettings = ({
  src,
  imageCropRectangle,
  naturalSize,
  box,
}: {
  src: string;
  imageCropRectangle: ImageCropRectangle;
  naturalSize: ImageNaturalSize | null;
  box: { width: number; height: number };
}) => {
  const scale = Math.min(
    box.width / imageCropRectangle.w,
    box.height / imageCropRectangle.h
  );

  return {
    width: imageCropRectangle.w * scale,
    height: imageCropRectangle.h * scale,
    backgroundImage: `url(${src})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: `${(naturalSize?.width ?? 0) * scale}px ${(naturalSize?.height ?? 0) * scale}px`,
    backgroundPosition: `${-imageCropRectangle.x * scale}px ${-imageCropRectangle.y * scale}px`,
  };
};

/**
 * Shows a sub-region of an image without slicing the file: the full image is
 * used as a background and scaled/offset so only `imageCropRectangle` is visible, centered and
 * aspect-preserved (contain) within the element.
 */
export const ImageCrop = ({
  src,
  imageCropRectangle,
  naturalSize,
  className,
}: ImageCropProps) => {
  const [elementRef, box] = useBoxDimensions<HTMLDivElement>();

  const ready =
    naturalSize && box && imageCropRectangle.w > 0 && imageCropRectangle.h > 0;

  const style = ready
    ? calculateCropCssSettings({
        src,
        imageCropRectangle,
        naturalSize,
        box,
      })
    : undefined;

  return (
    <div
      ref={elementRef}
      className={cn(
        'flex size-full items-center justify-center overflow-hidden',
        className
      )}>
      {ready ? <div style={style} /> : null}
    </div>
  );
};
